import { useEffect, useState } from "react";
import { UsersRound, Loader2, UserPlus, Trash2, Eye, EyeOff, BadgeCheck, PenLine, FileImage, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useRef } from "react";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SPECIALIZATIONS = ["Gynecology & Obstetrics", "Cosmetic & Reconstructive Gynecology", "Infertility (IVF)", "Laparoscopic Surgery", "General Medicine", "Anesthesia", "Neonatology"];
const EMPTY = { name: "", email: "", password: "", qualification: "", specialization: "Gynecology & Obstetrics", bio: "" };

export default function Team() {
    const { user } = useAuth();
    const [members, setMembers] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [showPw, setShowPw] = useState(false);
    const [saving, setSaving] = useState(false);
    const isOwner = !user?.clinic_id;

    const load = async () => {
        try {
            const { data } = await api.get("/team");
            setMembers(data);
        } catch (e) { toast.error(fmtErr(e)); }
    };

    useEffect(() => { load(); }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const add = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/team", form);
            toast.success(`Dr. login created for ${form.name}`);
            setForm(EMPTY);
            load();
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const resetPw = async (m) => {
        const pw = window.prompt(`Set a new password for ${m.name} (min 8 characters):`);
        if (!pw) return;
        try {
            await api.post(`/team/${m.id}/reset-password`, { password: pw });
            toast.success(`Password updated for ${m.name}. Share it with them privately.`);
        } catch (err) { toast.error(fmtErr(err)); }
    };

    const remove = async (m) => {
        if (!window.confirm(`Remove ${m.name} from the practice? Their records will be reassigned to you.`)) return;
        try {
            await api.delete(`/team/${m.id}`);
            toast.success("Member removed");
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    const sigInput = useRef(null);
    const sigTarget = useRef(null);
    const [sigBusy, setSigBusy] = useState(null);

    const onSigUpload = async (e) => {
        const f = e.target.files?.[0];
        const m = sigTarget.current;
        if (!f || !m) return;
        setSigBusy(m.id);
        try {
            const fd = new FormData();
            fd.append("file", f);
            await api.post(`/team/${m.id}/signature`, fd);
            toast.success(`Signature saved for ${m.name}`);
            load();
        } catch (err) { toast.error(fmtErr(err)); } finally { setSigBusy(null); e.target.value = ""; }
    };

    const viewSig = async (m) => {
        try {
            const res = await api.get(`/team/${m.id}/signature`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            window.open(url, "_blank");
        } catch (err) { toast.error(fmtErr(err)); }
    };

    const removeSig = async (m) => {
        if (!window.confirm(`Remove signature for ${m.name}?`)) return;
        try {
            await api.delete(`/team/${m.id}/signature`);
            toast.success("Signature removed");
            load();
        } catch (err) { toast.error(fmtErr(err)); }
    };

    return (
        <div className="space-y-5 animate-fade-up" data-testid="team-page">
            <div>
                <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Practice Team</h1>
                <p className="mt-1 text-base text-slate-500">Doctors in your practice share the same patient records, appointments and billing</p>
            </div>
            <input ref={sigInput} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" data-testid="signature-file-input" onChange={onSigUpload} />

            {isOwner && (
                <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm" data-testid="add-doctor-card">
                    <h3 className="font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2"><UserPlus className="h-4 w-4 text-[#E06D1F]" /> Add Doctor</h3>
                    <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-1.5"><Label>Full Name *</Label><Input data-testid="team-name-input" required value={form.name} onChange={set("name")} placeholder="Dr. Priya Shah" /></div>
                        <div className="space-y-1.5"><Label>Email (login) *</Label><Input data-testid="team-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="doctor@zenhospital.co.in" /></div>
                        <div className="space-y-1.5">
                            <Label>Password *</Label>
                            <div className="relative">
                                <Input data-testid="team-password-input" type={showPw ? "text" : "password"} required minLength={6} value={form.password} onChange={set("password")} className="pr-10" placeholder="Min 6 characters" />
                                <button type="button" data-testid="team-password-eye" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                            </div>
                        </div>
                        <div className="space-y-1.5"><Label>Qualification</Label><Input data-testid="team-qualification-input" value={form.qualification} onChange={set("qualification")} placeholder="MBBS, MS" /></div>
                        <div className="space-y-1.5">
                            <Label>Specialization</Label>
                            <select data-testid="team-specialization-select" value={form.specialization} onChange={set("specialization")} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none">
                                {SPECIALIZATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1.5 sm:col-span-2 lg:col-span-3"><Label>Bio / Expertise</Label><Input data-testid="team-bio-input" value={form.bio} onChange={set("bio")} placeholder="e.g. Anaesthesiologist & Intensivist; special interests..." /></div>
                        <div className="flex items-end">
                            <Button data-testid="team-add-btn" type="submit" disabled={saving} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Add to Practice</Button>
                        </div>
                    </form>
                </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="team-list-card">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
                    <UsersRound className="h-4 w-4 text-[#E06D1F]" />
                    <h3 className="font-heading font-semibold text-slate-900">Doctors</h3>
                </div>
                {!members ? (
                    <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {members.map((m) => (
                            <div key={m.id} data-testid={`team-member-${m.id}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                                <div className="flex items-center gap-3">
                                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF4EC] text-[#E06D1F] font-heading font-bold text-sm">{m.name.replace(/^Dr\.?\s*/i, "").charAt(0)}</span>
                                    <div>
                                        <p className="text-sm font-semibold text-slate-900">
                                            {m.name?.toLowerCase().startsWith("dr") ? m.name : `Dr. ${m.name}`}
                                            {m.is_owner && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-700"><BadgeCheck className="h-3 w-3" /> OWNER</span>}
                                        </p>
                                        <p className="text-xs text-slate-500">{[m.qualification, m.specialization].filter(Boolean).join(" · ")} · {m.email}</p>
                                        {m.bio && <p className="text-xs text-slate-400 mt-0.5 max-w-xl" data-testid={`team-bio-${m.id}`}>{m.bio}</p>}
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {(isOwner || m.id === user?.id) && (
                                        <>
                                            <Button data-testid={`sig-upload-${m.id}`} size="sm" variant="outline" onClick={() => { sigTarget.current = m; sigInput.current?.click(); }} disabled={sigBusy === m.id} className="gap-1.5 text-xs">
                                                {sigBusy === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PenLine className="h-3.5 w-3.5" />} {m.has_signature ? "Replace Sign" : "Upload Sign"}
                                            </Button>
                                            {m.has_signature && (
                                                <>
                                                    <Button data-testid={`sig-view-${m.id}`} size="sm" variant="ghost" onClick={() => viewSig(m)} className="text-slate-500 hover:text-[#E06D1F] gap-1 text-xs"><FileImage className="h-3.5 w-3.5" /> View</Button>
                                                    <Button data-testid={`sig-remove-${m.id}`} size="sm" variant="ghost" onClick={() => removeSig(m)} className="text-slate-400 hover:text-red-600 text-xs">Remove Sign</Button>
                                                </>
                                            )}
                                        </>
                                    )}
                                    {m.has_signature && <span className="rounded bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`sig-badge-${m.id}`}>SIGN ON FILE</span>}
                                    {isOwner && !m.is_owner && (
                                        <>
                                            <Button data-testid={`team-resetpw-${m.id}`} size="sm" variant="outline" onClick={() => resetPw(m)} className="gap-1.5 text-xs"><KeyRound className="h-3.5 w-3.5" /> Reset Password</Button>
                                            <Button data-testid={`team-remove-${m.id}`} size="sm" variant="ghost" onClick={() => remove(m)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {!isOwner && <p className="text-xs text-slate-400" data-testid="team-staff-note">Only the practice owner can add or remove doctors.</p>}
        </div>
    );
}
